///
/// EarthBrowser
///
/// Created by Matt Giger on 10/10/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Single analytics entry point for the main app. `track()` is the only
/// function components and middleware call; it fans out to:
///
///   - Tier 1: first-party metrics (AnalyticsCore → POST /api/metrics),
///     always on, identifier-free.
///   - Tier 2: PostHog, only for events in cPostHogEvents and only when the
///     SDK is initialized and the visitor has performance consent (the SDK
///     enforces its own opt-out state).
///
/// This module also owns the session model: visibility- and input-aware
/// active time, a 60-second heartbeat carrying the enabled layers and a
/// frame-time sample, first-interaction and first-frame timing, and the
/// session_ended summary on pagehide. Nothing here is persisted.
///
/// Design: documentation/planning/ANALYTICS_STRATEGY.md §4.2
///

import { Constants } from "@/constants"
import { underMemoryPressure } from "@/earthbrowser/utils/MemoryBudget"
import { nativePlatform } from "@/native"
import { isDevelopment, isTauriApp } from "@/utils/environment"
import { hasPerformanceConsent } from "@/utils/privacy-consent"
import type { AnalyticsEntry, AnalyticsEventName, AnalyticsEventProps, AnalyticsPlatform } from "@shared-types"
import { cPostHogEvents } from "@shared-types"
import { textureMemoryStats } from "@stovokor/shader/TextureMemory"
import posthog from "posthog-js"
import { AnalyticsCore, externalReferrerHost } from "./analyticsCore"

// ---- tunables ----

/** Input within this window counts the current second as "active" */
const cIdleAfterMs = 60_000
/** Heartbeat (with layer snapshot + perf sample) every N active seconds */
const cHeartbeatEveryS = 60
/** entity_selected is high-frequency; keep one in N */
const cEntitySelectedSampleRate = 1 / 5
/** Share of sessions that run the frame-time sampler, by device class */
const cPerfSampleRateDesktop = 0.2
const cPerfSampleRateMobile = 0.5
/** A frame slower than this counts as a long frame */
const cLongFrameMs = 100

/**
 * §4.3 fallback switch. When true AND performance consent is granted, the
 * batch carries a subject id (eb_anon_id or "u:<id>") that the server
 * HMACs into app_event.subject_hash so funnels/retention can be computed
 * in Postgres without PostHog. Off while PostHog Cloud is the Tier 2.
 */
const cSendSubject = false

// ---- module state ----

let core: AnalyticsCore | null = null
let loadedAt = 0
let activeMs = 0
let lastInputAt = 0
let lastHeartbeatAtActiveS = 0
let interactions = 0
let windowsOpened = 0
let firstInteractionSent = false
let appReadySent = false
let layersMax = 0
const enabledLayers = new Map<string, number>()
const onceEvents = new Set<AnalyticsEventName>()
let frameTimes: number[] = []
let perfSampling = false

function isMobile(): boolean {
	return typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
}

function detectEntry(): { entry: AnalyticsEntry; referrer_host?: string } {
	const params = new URL(window.location.href).searchParams
	const referrer_host = externalReferrerHost()
	if (params.has("s")) return { entry: "share", referrer_host }
	const target = params.get("target") ?? ""
	if (target.startsWith("story-")) return { entry: "story", referrer_host }
	if (params.has("camera") || params.has("target") || params.has("layers")) return { entry: "deeplink", referrer_host }
	if (params.has("lat") && params.has("lon")) return { entry: "deeplink", referrer_host }
	if (referrer_host) return { entry: "referral", referrer_host }
	return { entry: "organic" }
}

function viewportBucket(): string {
	const w = window.innerWidth
	if (w < 480) return "xs"
	if (w < 768) return "s"
	if (w < 1280) return "m"
	if (w < 1920) return "l"
	return "xl"
}

/** First launch on this install: no persisted redux state yet */
function isColdInstall(): boolean {
	try {
		return localStorage.getItem(`persist:earthbrowser_${Constants.persistStorageVersion}`) === null
	} catch {
		return true
	}
}

function gpuSnapshot() {
	const stats = textureMemoryStats()
	const mb = 1024 * 1024
	return {
		gpu_mb: Math.round(stats.bytes / mb),
		gpu_peak_mb: Math.round(stats.peakBytes / mb),
		under_pressure: underMemoryPressure(),
	}
}

/** p50/p10 FPS and long-frame count from the frame times since last call */
function perfSnapshot(): { fps_p50?: number; fps_p10?: number; long_frames?: number } {
	if (!perfSampling || frameTimes.length < 30) {
		frameTimes = []
		return {}
	}
	const sorted = [...frameTimes].sort((a, b) => a - b)
	const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))]
	const long_frames = frameTimes.filter(ms => ms > cLongFrameMs).length
	frameTimes = []
	return {
		fps_p50: Math.round(1000 / at(0.5)),
		// 10th-percentile FPS = 90th-percentile frame time
		fps_p10: Math.round(1000 / at(0.9)),
		long_frames,
	}
}

function sendHeartbeat(): void {
	track("session_heartbeat", {
		active_s: Math.round(activeMs / 1000),
		layers: [...enabledLayers.keys()],
		...perfSnapshot(),
		...gpuSnapshot(),
	})
}

function noteInput(kind: AnalyticsEventProps["first_interaction"]["kind"]): void {
	lastInputAt = performance.now()
	interactions += 1
	if (!firstInteractionSent) {
		firstInteractionSent = true
		track("first_interaction", { since_load_ms: Math.round(performance.now() - loadedAt), kind })
	}
}

function startActivityTracking(): void {
	const opts: AddEventListenerOptions = { passive: true, capture: true }
	window.addEventListener("pointerdown", () => noteInput("pointer"), opts)
	window.addEventListener("wheel", () => noteInput("wheel"), opts)
	window.addEventListener("touchstart", () => noteInput("touch"), opts)
	window.addEventListener("keydown", () => noteInput("key"), opts)

	// One-second ticker: count the second as active when the tab is visible
	// and the visitor has touched something recently. Heartbeat on every
	// cHeartbeatEveryS of accumulated active time.
	setInterval(() => {
		if (document.visibilityState !== "visible") return
		if (performance.now() - lastInputAt > cIdleAfterMs) return
		activeMs += 1000
		const activeS = Math.floor(activeMs / 1000)
		if (activeS - lastHeartbeatAtActiveS >= cHeartbeatEveryS) {
			lastHeartbeatAtActiveS = activeS
			sendHeartbeat()
		}
	}, 1000)

	window.addEventListener("pagehide", () => {
		track("session_ended", {
			active_s: Math.round(activeMs / 1000),
			interactions,
			layers_max: layersMax,
			windows_opened: windowsOpened,
		})
		core?.flush()
	})
}

function startPerfSampling(): void {
	const rate = isMobile() ? cPerfSampleRateMobile : cPerfSampleRateDesktop
	perfSampling = Math.random() < rate
	if (!perfSampling) return

	let last = performance.now()
	const tick = (now: number) => {
		const dt = now - last
		last = now
		// Ignore the gap after a hidden tab resumes
		if (document.visibilityState === "visible" && dt < 5000) {
			frameTimes.push(dt)
			if (frameTimes.length > 4000) frameTimes.shift()
		}
		requestAnimationFrame(tick)
	}
	requestAnimationFrame(tick)
}

// ---- public API ----

/** Called once from main.tsx before React mounts */
export function initAnalytics(): void {
	if (core || typeof window === "undefined") return
	loadedAt = performance.now()
	// The load itself counts as activity for the first idle window
	lastInputAt = loadedAt
	const params = new URL(window.location.href).searchParams

	core = new AnalyticsCore({
		platform: launchPlatform(),
		appVersion: __APP_VERSION__,
		apiUrl: import.meta.env.VITE_API_URL || "",
		debug: isDevelopment(),
		enabled: params.get("metrics") !== "off",
	})

	// Tier 2 sink: PostHog sees the funnel/activation subset, and only once
	// its SDK is initialized (it enforces consent/opt-out itself)
	core.onEvent(event => {
		if (!cPostHogEvents.has(event.event)) return
		if (!(posthog as unknown as { __loaded?: boolean }).__loaded) return
		posthog.capture(event.event, event.props)
	})

	const { entry, referrer_host } = detectEntry()
	track("app_launched", {
		entry,
		referrer_host,
		utm_source: params.get("utm_source") ?? undefined,
		utm_medium: params.get("utm_medium") ?? undefined,
		utm_campaign: params.get("utm_campaign") ?? undefined,
		route: window.location.pathname,
		viewport: viewportBucket(),
		cold: isColdInstall(),
	})

	startActivityTracking()
	startPerfSampling()
}

/**
 * Record an event. Safe to call before initAnalytics (dropped) and from
 * anywhere in the app; never throws.
 */
export function track<E extends AnalyticsEventName>(event: E, props: AnalyticsEventProps[E]): void {
	if (!core) return
	if (event === "entity_selected" && Math.random() >= cEntitySelectedSampleRate) return
	// Once-per-session events
	if (event === "app_ready" || event === "first_interaction") {
		if (onceEvents.has(event)) return
		onceEvents.add(event)
	}
	try {
		core.track(event, props)
	} catch { /* analytics is best-effort */ }
}

/** Engine has rendered its first frame (called from EarthBrowserCanvas) */
export function markAppReady(): void {
	if (appReadySent || !core) return
	appReadySent = true
	const nav = navigator as Navigator & { deviceMemory?: number }
	track("app_ready", {
		first_frame_ms: Math.round(performance.now() - loadedAt),
		hw_threads: navigator.hardwareConcurrency ? Math.min(32, navigator.hardwareConcurrency) : undefined,
		device_memory_gb: nav.deviceMemory,
	})
}

/** Dev tooling (window.__ebDebug.getMetrics()): transport + session state */
export function analyticsDebugSnapshot() {
	return {
		...(core?.snapshot() ?? { enabled: false, queued: 0, sentBatches: 0, sentEvents: 0, recent: [] }),
		activeS: Math.round(activeMs / 1000),
		interactions,
		enabledLayers: [...enabledLayers.keys()],
		perfSampling,
		frameSamples: frameTimes.length,
	}
}

/** Force a flush (dev tooling) */
export function flushAnalytics(): void {
	core?.flush()
}

/** Entry classification of this launch (also sent with the anon session call) */
export function launchEntry(): AnalyticsEntry {
	return typeof window === "undefined" ? "organic" : detectEntry().entry
}
export function launchPlatform(): AnalyticsPlatform {
	return nativePlatform() ?? (isTauriApp() ? "tauri" : "web")
}

/**
 * True when the visitor touched something in the last `withinMs`. Used by
 * the middleware to tell user-driven actions from ones the engine or
 * rehydration dispatches on its own (clock ticks, persisted layers).
 */
export function recentInput(withinMs = 2000): boolean {
	return firstInteractionSent && performance.now() - lastInputAt < withinMs
}

/** Seconds since the page started loading (for since_load_s props) */
export function secondsSinceLoad(): number {
	return Math.round((performance.now() - loadedAt) / 1000)
}

/** Layer bookkeeping used by analyticsMiddleware; returns dwell on disable */
export function noteLayerEnabled(layer: string): void {
	if (!enabledLayers.has(layer)) {
		enabledLayers.set(layer, performance.now())
		layersMax = Math.max(layersMax, enabledLayers.size)
	}
}
export function noteLayerDisabled(layer: string): number {
	const since = enabledLayers.get(layer)
	enabledLayers.delete(layer)
	return since === undefined ? 0 : Math.round((performance.now() - since) / 1000)
}
export function noteWindowOpened(): void {
	windowsOpened += 1
}
/** UI-originated activity that isn't a raw input event (e.g. a command) */
export function noteUiInteraction(): void {
	noteInput("ui")
}

/**
 * §4.3 fallback: attach (or clear) the consented subject id. No-op unless
 * cSendSubject is on and the visitor has performance consent.
 */
export function setAnalyticsSubject(subject: string | null): void {
	if (!core) return
	core.setSubject(cSendSubject && subject && hasPerformanceConsent() ? subject : null)
}